import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, MailCheck, ArrowLeft } from "lucide-react";

const LOGO_URL = "/logo.png";
const inputClass = "bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#D4AF37]";

const errorText = (error, fallback) => {
  const d = error?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const { login, verifyOtp, user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    navigate("/admin");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.otp_required) {
        setChallenge(result.challenge_id);
        setCode("");
        toast.success("Un code de vérification vient d'être envoyé à l'adresse de l'agence.");
      } else {
        toast.success("Connexion réussie !");
        navigate("/admin");
      }
    } catch (error) {
      toast.error(errorText(error, error?.response?.status === 401 ? "Identifiants incorrects" : "Connexion impossible. Réessayez."));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyOtp(challenge, code);
      toast.success("Connexion réussie !");
      navigate("/admin");
    } catch (error) {
      const msg = errorText(error, "Code incorrect");
      toast.error(msg);
      if (/expiré|tentatives/i.test(msg)) {
        setChallenge(null);
        setCode("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <Link to="/">
            <img src={LOGO_URL} alt="MAINGUY IMMOBILIER" className="h-40 w-auto mx-auto mb-6 bg-white rounded-lg p-3" />
          </Link>
          <h1 className="font-serif text-2xl text-white mb-2">Espace Administration</h1>
          <p className="text-gray-500 text-sm">{challenge ? "Vérification en deux étapes" : "Connectez-vous pour gérer votre site"}</p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-sm">
          {challenge ? (
            <form onSubmit={handleVerify} className="space-y-6" data-testid="admin-otp-form">
              <div className="flex items-start gap-3 text-sm text-gray-300">
                <MailCheck size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <p>Un code à 6 chiffres a été envoyé sur la boîte mail de l'agence. Il est valable 10 minutes.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-gray-300">Code de vérification</Label>
                <Input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  required
                  autoFocus
                  className={`${inputClass} text-center text-2xl tracking-[0.5em] font-mono`}
                  data-testid="admin-otp-code"
                />
              </div>
              <Button type="submit" className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#AA771C] font-semibold py-6" disabled={loading || code.length !== 6} data-testid="admin-otp-submit">
                {loading ? "Vérification..." : <><Lock size={18} className="mr-2" />Valider le code</>}
              </Button>
              <button type="button" onClick={() => { setChallenge(null); setCode(""); }} className="w-full text-sm text-gray-400 hover:text-white flex items-center justify-center gap-2" data-testid="admin-otp-back">
                <ArrowLeft size={16} /> Revenir à la connexion
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@adresse.fr" required className={inputClass} data-testid="admin-login-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300">Mot de passe</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className={`${inputClass} pr-10`} data-testid="admin-login-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white" aria-label="Afficher le mot de passe">
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#AA771C] font-semibold py-6" disabled={loading} data-testid="admin-login-submit">
                {loading ? "Connexion..." : <><Lock size={18} className="mr-2" />Se connecter</>}
              </Button>
              <p className="text-xs text-gray-500 text-center">Après validation du mot de passe, un code de vérification est envoyé par e-mail à l'agence.</p>
            </form>
          )}
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-gray-500 hover:text-white text-sm transition-colors" data-testid="back-to-site-link">← Retour au site</Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
